import React, { ChangeEvent, FunctionComponent, ReactNode, useCallback, useRef, useState } from 'react';

import { ThemeName } from '@ps-int/general-themes';
import { Button as OrbButton } from '@yandex-360/orb-components/dist/components/Button';
import { ScopedTheme } from '@yandex-360/orb-components/dist/components/Theme/ScopedTheme';
import { Tooltip, TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../init-store';
import Button from '../../../../shared/components/button';
import Footer from '../../../../shared/components/footer';
import { ProgressText } from '../../../../shared/components/progress-text';
import Spin from '../../../../shared/components/spin';
import SvgImage from '../../../../shared/components/svg-image';
import TextInput from '../../../../shared/components/text-input';
import { SELF } from '../../../../shared/constants';
import {
    getThemeName,
    isLightTheme,
    isMessengerIntegration,
    isWithoutRedirect,
    selectIsGlobalBarEnabled,
    selectIsMeetingFullscreenEnabled,
    selectIsMeetingRoomIntegration,
    selectIsTeleMessenger,
    selectShowUserMismatchesOrganization,
} from '../../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import { getPremiumButtonEnabled } from '../../../../shared/features/root/selectors';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { i18n } from '../../../../shared/helpers/i18n';
import { ACTION, FEATURE } from '../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { useOnMount } from '../../../../shared/hooks/useOnMount';
import { useTypedSelector } from '../../../../shared/hooks/useTypedSelector';
import { BroadcastStatus } from '../../../../types';
import { ConferenceStatus } from '../../constants';
import { cancelCallAction, fetchMe, startAsGuestConferenceAction } from '../../features/conference/slice';
import { setIsFullscreenAction } from '../../features/fullscreen';
import { ConferenceGuestConfig } from '../../features/toolbar';
import { OutgoingEvents } from '../../iframe-sagas/types';
import { getConferenceStatus, getParticipant } from '../../selectors';
import { DisconnectReason } from '../../types';
import { GoloomJoinParticipantContainer } from '../containers/GoloomJoinParticipantContainer';
import css from './index.css';
import { OverlayChangeAccount } from './OverlayChangeAccount';

const HIDE_OVERLAY_MESSAGE_TIMEOUT = 15000;

const logger = createLogger('overlay');

const closeIcon = <SvgImage imageName="close" />;
const spinIcon = <Spin size="md" progress color="black" />;

const mapState = (state: RootState) => {
    const { user, config, sessionId } = state;
    const conferenceStatus = getConferenceStatus(state);
    const self = getParticipant(state, SELF);
    const isMessenger = isMessengerIntegration(state);
    const isTeleMessenger = selectIsTeleMessenger(state);
    return {
        isAnonymous: !user,
        agreementShown: config.agreementShown,
        broadcastStatus: config.broadcastStatus,
        canJoin: conferenceStatus === ConferenceStatus.READY_TO_JOIN,
        isJoining: conferenceStatus === ConferenceStatus.JOINING,
        isWaitingRoom: conferenceStatus === ConferenceStatus.WAITING_ROOM,
        audioMuted: Boolean(!self.audioTrackState || self.audioTrackState?.muted),
        videoMuted: Boolean(!self.videoTrackState || self.videoTrackState?.muted),
        sessionId,
        isMessengerIntegration: isMessenger,
        isLightTheme: isLightTheme(state),
        isDefaultTheme: getThemeName(state) === ThemeName.GeneralDefault && !isMessenger,
        isWithoutRedirect: isWithoutRedirect(state),
        isTeleMessenger,
        isMeetingFullscreenEnabled: selectIsMeetingFullscreenEnabled(state),
    };
};

const connector = connect(mapState, {
    startAsGuestConference: startAsGuestConferenceAction,
    setIsFullscreen: setIsFullscreenAction,
    cancelCall: cancelCallAction,
    fetchMe,
});

export type ToolbarContentProps = {
    centerContent: ReactNode;
    onClickCancel?: () => void;
    isOverlay?: boolean;
};

type Props = ConnectedProps<typeof connector> & {
    roomId: string;
    waitContinueInBrowser: boolean;
    onContinueInBrowser: () => void;
    toolbarContent: (props: ToolbarContentProps) => ReactNode;
    shouldShowPreloader: boolean;
};

const getYouAreJoiningText = (shouldBreak: boolean, hasAnonymousTail = false): string =>
    i18n('overlay', 'you-are-joining-text', {
        spacebreak: shouldBreak ? '\n' : ' ',
        showTail: hasAnonymousTail,
    });

const Overlay: FunctionComponent<Props> = ({
    audioMuted,
    videoMuted,
    isAnonymous,
    roomId,
    sessionId,
    waitContinueInBrowser,
    onContinueInBrowser,
    fetchMe,
    canJoin,
    isJoining,
    isWaitingRoom,
    toolbarContent,
    shouldShowPreloader,
    startAsGuestConference,
    cancelCall,
    broadcastStatus,
    isMessengerIntegration,
    isLightTheme,
    isDefaultTheme,
    isWithoutRedirect,
    isTeleMessenger,
    isMeetingFullscreenEnabled,
    setIsFullscreen,
}) => {
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);

    const containerRef = useRef<HTMLDivElement>(null);

    const isMobile = useSelector(isMobileUa);

    const isGlobalBarEnabled = useSelector(selectIsGlobalBarEnabled);

    const broadcastCreated =
        broadcastStatus && [BroadcastStatus.CREATED, BroadcastStatus.FINISHED].includes(broadcastStatus);
    const broadcastStarted =
        broadcastStatus && [BroadcastStatus.STARTED, BroadcastStatus.STARTING].includes(broadcastStatus);

    const [showMessage, setShowMessage] = useState<boolean>(Boolean(isWithoutRedirect));
    const closeMessage = useCallback(() => {
        setShowMessage(false);
    }, []);

    const user = useTypedSelector((state) => state.user);
    const showUserMismatchesOrganization = useSelector(selectShowUserMismatchesOrganization);

    useOnMount(() => {
        fetchMe();

        logger.featureEvent({
            feature: FEATURE.CONNECTION,
            action: ACTION.JOIN_SETTINGS_SHOWN,
            options: {
                additional: {
                    roomId,
                    sessionId,
                    uid: user?.uid,
                },
            },
        });

        countMetrika('connection_screen', 'show', roomId);
        countMetrika('connection_screen', 'session', sessionId);
        if (broadcastCreated) {
            countMetrika('connection_screen', 'is_stream', 'off');
        }
        if (broadcastStarted) {
            countMetrika('connection_screen', 'is_stream', 'on');
        }

        if (showMessage) {
            setTimeout(closeMessage, HIDE_OVERLAY_MESSAGE_TIMEOUT);
        }
    });

    const [displayName, setDisplayName] = useState(i18n('common', 'guest'));

    const updateDisplayNameFromInputEvent = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setDisplayName(event.target.value);
        },
        [setDisplayName]
    );

    const messageBoxAnchorRef = useRef<HTMLDivElement>(null);

    const handleCancelCallClick = useCallback(() => {
        if (isMessengerIntegration || isTeleMessenger) {
            parentFrameCommunicator.sendMessage('messenger', {
                event: OutgoingEvents.CLOSE_JOIN_SCREEN,
                payload: {
                    source: DisconnectReason.END_CALL_BUTTON,
                },
            });

            return;
        }

        cancelCall();
    }, [cancelCall, isMessengerIntegration, isTeleMessenger]);

    let content;
    if (waitContinueInBrowser && !shouldShowPreloader) {
        content = (
            <div className={css.waitContinueContainer}>
                <div className={css.waitContinueText}>{getYouAreJoiningText(true)}</div>
                <OrbButton view="brand" size="lg" className={css.continueInBrowserButton} onPress={onContinueInBrowser}>
                    {i18n('common', 'continue-in-browser')}
                </OrbButton>
            </div>
        );
    } else if ((!canJoin && !isJoining && !isWaitingRoom) || shouldShowPreloader) {
        content = (
            <div className={css.spinnerContainer}>
                <Spin size="xl" color={isLightTheme ? undefined : 'white'} progress />
            </div>
        );
    } else {
        const onClickStartCall = () => {
            const options: ConferenceGuestConfig = {
                audioMuted,
                videoMuted,
            };

            if (isAnonymous) {
                options.displayName = displayName;
            }
            if (isMeetingFullscreenEnabled) {
                setIsFullscreen(true);
            }
            startAsGuestConference(options);

            countMetrika('connection_screen', 'connect', `${isAnonymous ? 'no' : 'with'}_account`);
            countMetrika(
                'connection_screen',
                'connect',
                `mic_${audioMuted ? 'off' : 'on'}_cam_${videoMuted ? 'off' : 'on'}`
            );
        };

        const joinButton = (
            <Button
                className={css.joinMeetingButton}
                isDisabled={!canJoin && !isJoining}
                view="brand"
                size="lg"
                onClick={onClickStartCall}
                testId="enter-conference-button"
                icon={isJoining ? spinIcon : undefined}
            >
                {isJoining ? null : i18n('overlay', 'join-button')}
            </Button>
        );

        const connectionStatusBlock = (
            <ProgressText
                className={cn(css.connectionStatusTextWrapper, {})}
                textClassName={css.connectionStatusText}
                texts={[i18n('overlay', 'waiting-room-status-1'), i18n('overlay', 'waiting-room-status-2')]}
            />
        );

        const participantContent = (
            <div className={css.participantContent}>
                {isAnonymous && !(isWaitingRoom && isMobile) && !isMeetingRoomIntegration ? (
                    <div className={css.displayNameBlock}>
                        <div className={css.displayNameInputTitle}>{i18n('overlay', 'nickname-title-label')}</div>
                        <TextInput
                            autoFocus={true}
                            onChange={updateDisplayNameFromInputEvent}
                            className={css.displayNameInput}
                            value={displayName}
                            readOnly={isWaitingRoom}
                            size="lg"
                        />
                    </div>
                ) : null}
                {user?.displayName && !isMeetingRoomIntegration ? (
                    <div className={css.userInfoBlock}>
                        <div className={css.userDisplayName}>{user.displayName}</div>
                        {Boolean(!isMessengerIntegration && user?.login) && (
                            <div className={css.userLogin}>{user.login}</div>
                        )}
                    </div>
                ) : null}
                {isWaitingRoom && (isMobile || isMeetingRoomIntegration) ? connectionStatusBlock : null}
                {isMeetingRoomIntegration ? null : (
                    <div className={css.toolbarWrapper}>
                        {toolbarContent({
                            centerContent: isWaitingRoom ? (isMobile ? null : connectionStatusBlock) : joinButton,
                            onClickCancel:
                                isMessengerIntegration || isTeleMessenger ? handleCancelCallClick : undefined,
                            isOverlay: true,
                        })}
                    </div>
                )}
            </div>
        );

        content = (
            <>
                <div className={css.messageBoxAnchor} ref={messageBoxAnchorRef} />
                {showMessage ? (
                    <Tooltip
                        isOpen={true}
                        triggerRef={messageBoxAnchorRef}
                        placement="bottom"
                        offset={12}
                        className={css.overlayTooltip}
                        onOpenChange={closeMessage}
                    >
                        <div className={css.overlayTooltipText}>{i18n('overlay', 'quality-message')}</div>
                    </Tooltip>
                ) : null}
                <GoloomJoinParticipantContainer>{participantContent}</GoloomJoinParticipantContainer>
            </>
        );
    }

    const shouldShowVideoPreview =
        !waitContinueInBrowser && (canJoin || isJoining || isWaitingRoom) && !shouldShowPreloader;
    const showOverlayChangeAccount = showUserMismatchesOrganization && !isMobile && shouldShowVideoPreview;

    const contentOuterWrapper = (
        <div
            className={cn(css.contentOuterWrapper, {
                [css.withOverlayChangeAccount]: showOverlayChangeAccount,
                [css.withGlobalBar]: isGlobalBarEnabled,
            })}
            ref={containerRef}
        >
            {shouldShowVideoPreview && !isMessengerIntegration && !isMeetingRoomIntegration && (
                <TooltipStateful content={i18n('common', 'to-home-screen')} trigger="hover">
                    <div>
                        <Button
                            view="neutral"
                            pin="round-round"
                            size="lg"
                            className={css.closeButton}
                            onClick={handleCancelCallClick}
                            testId="to-home-screen-button"
                            icon={closeIcon}
                        />
                    </div>
                </TooltipStateful>
            )}
            <div
                className={cn(css.contentInnerWrapper, {
                    [css.contentInnerWrapper_videoPreview]: shouldShowVideoPreview,
                    [css.contentInnerWrapper_defaultTheme]: isDefaultTheme,
                })}
            >
                {content}
            </div>

            {showOverlayChangeAccount && (
                <div className={css.changeAccountBlock}>
                    <OverlayChangeAccount />
                </div>
            )}
        </div>
    );

    const isPremiumButtonEnabled = useSelector(getPremiumButtonEnabled);

    return (
        <div
            className={cn(css.overlayContainer, {
                [css.overlayContainerJoin]: shouldShowVideoPreview && !isGlobalBarEnabled,
                [css.withPaddings]: isGlobalBarEnabled,
                [css.premiumButtonDisabled]: !isPremiumButtonEnabled,
            })}
        >
            {shouldShowVideoPreview ? (
                <ScopedTheme theme="dark">{contentOuterWrapper}</ScopedTheme>
            ) : (
                contentOuterWrapper
            )}
            {!isMessengerIntegration && !isMeetingRoomIntegration && !isTeleMessenger ? (
                <Footer hideSettings={!waitContinueInBrowser} />
            ) : null}
        </div>
    );
};

export default connector(Overlay);
